import {
  faXmark as CancelIcon,
  faExclamationTriangle,
  faTriangleExclamation as WarningIcon
} from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowUpRightFromSquare as externalLinkIcon,
  faArrowLeft,
  faArrowRight
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  CircularProgress,
  Box as MuiBox,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { DOCS_URL } from '@/constants/common';
import useConfirm from '@/hooks/useConfirm';
import {
  selectedCloudSourceAtom,
  selectedSourceAtom,
  sourceStatusAtom
} from '@/pages/Administration/Devices/Devices.store';
import { CLOSE_REASON } from '@/pages/Administration/Devices/Devices.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import AddAccessCode from '../AddAccessCode/AddAccessCode';
import AddThumbprintId from '../AddThumbprintId/AddThumbprintId';
import { CloudAPILinks } from '../available-doc-links.constant';
import SOURCE_TYPE, { WIZARD_STEP } from '../SourceCreate.constants';
import { useSourceDialog } from '../SourceCreate.hooks';
import {
  connectionInputSchemaAtom,
  deviceAlertFieldsAtom,
  originalSourceTypesAtom,
  selectedCategoryAtom,
  selectedCloudProviderAtom,
  selectedRegionAtom,
  selectedSourceDetailsAtom,
  selectedSourceTypeIdAtom,
  selectedSubSourceTypeAtom,
  selectedVulnFieldsAtom,
  sourceResponseAtom,
  sourceTypeBasicAtom,
  tieredSourceTypesAtom,
  wizardCurrentStepAtom,
  wizardSelectedSourceAtom
} from '../SourceCreate.store';
import {
  type SourceCreateDynamicFormModel,
  SourceDialogMode
} from '../SourceCreate.types';
import SourceCreateForm from '../SourceCreateForm';
import SourceSelectionStep from '../SourceSelectionStep';
import useStyles from './SourceCreateDialog.styles';
import {
  buildSetupInstructionsUrl,
  determineButtonLabel,
  determineDialogTitle
} from './SourceCreateDialog.utils';
import { useDataProviderValidation } from './useDataProviderValidation.hook';
import { useDialogCleanup } from './useDialogCleanup.hook';
import { useDialogState } from './useDialogState.hook';
import { useFormReset } from './useFormReset.hook';
import { useSourceSubmission } from './useSourceSubmission.hook';
import { useWizardDataProviderPreset } from './useWizardDataProviderPreset.hook';
import { useWizardNavigation } from './useWizardNavigation.hook';

type SourceCreateDialogProps = {
  isCustomerCloudSourcePage?: boolean;
};

const SourceCreateDialog = ({
  isCustomerCloudSourcePage = false
}: SourceCreateDialogProps) => {
  const formMethods = useForm<SourceCreateDynamicFormModel>({
    mode: 'onChange'
  });
  const {
    handleSubmit,
    reset,
    trigger,
    setValue,
    formState: { errors },
    unregister,
    getValues,
    clearErrors,
    control
  } = formMethods;

  const {
    error,
    setError,
    mutation,
    open: showSourcePopup,
    mode,
    authenticationMode,
    newCertificate,
    regenerateCertificate,
    closeSourceDialog,
    refreshDeviceListing,
    getNewlyAddedCategories,
    getNewlyAddedRegions,
    getNewlyAddedCloudProviders,
    getRemovedCategories,
    getRemovedRegions
  } = useSourceDialog();

  const { classes } = useStyles({ mode });
  const [selectedSource] = useAtom(selectedSourceAtom);
  const [selectedCloudSource] = useAtom(selectedCloudSourceAtom);
  const [sourceResponse] = useAtom(sourceResponseAtom);
  const [selectedSourceTypeId, setSelectedSourceTypeId] = useAtom(
    selectedSourceTypeIdAtom
  );
  const [selectedSourceDetails, setSelectedSourceDetails] = useAtom(
    selectedSourceDetailsAtom
  );
  const [categories, setCategories] = useAtom(selectedCategoryAtom);
  const [regions, setRegions] = useAtom(selectedRegionAtom);
  const [cloudProviders, setCloudProviders] = useAtom(
    selectedCloudProviderAtom
  );
  const [, setDeviceAlertFields] = useAtom(deviceAlertFieldsAtom);
  const [, setVulnFields] = useAtom(selectedVulnFieldsAtom);
  const [connectionInputSchema, setConnectionInputSchema] = useAtom(
    connectionInputSchemaAtom
  );
  const [sourceStatus] = useAtom(sourceStatusAtom);
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [scroll] = useState<DialogProps['scroll']>('paper');
  const [sourceTypes] = useAtom(sourceTypeBasicAtom);
  const [selectedSubSourceType] = useAtom(selectedSubSourceTypeAtom);
  const [tieredSourceTypes] = useAtom(tieredSourceTypesAtom);
  const [originalSourceTypes] = useAtom(originalSourceTypesAtom);

  // Dialog state management (includes Auto SOC validation)
  const {
    dialogConfig,
    setDialogConfig,
    isSubmitting,
    setIsSubmitting,
    isAutoSocUnsupportedSourceSelected
  } = useDialogState({
    selectedSourceTypeId,
    tieredSourceTypes
  });

  const {
    ConfirmationModal: CreateSourceConfirmationModal,
    confirm: confirmSave
  } = useConfirm(dialogConfig);

  // Wizard state
  const [currentStep, setCurrentStep] = useAtom(wizardCurrentStepAtom);
  const [wizardSelectedSource, setWizardSelectedSource] = useAtom(
    wizardSelectedSourceAtom
  );

  // Form reset management
  useFormReset({
    mode,
    currentStep,
    showSourcePopup,
    getValues,
    reset,
    unregister,
    selectedOrgId: selectedOrgData?.id,
    setError,
    setCategories,
    setRegions,
    setCloudProviders,
    setDeviceAlertFields,
    setVulnFields,
    setSelectedSourceTypeId,
    setWizardSelectedSource,
    setConnectionInputSchema
  });

  // Data provider validation (includes auto-update of validation field)
  useDataProviderValidation({
    setValue,
    clearErrors,
    control,
    selectedSourceTypeId,
    mode,
    selectedSourceDetails
  });

  // Dialog cleanup and close handlers
  const { handleClose } = useDialogCleanup({
    mode,
    reset,
    setSelectedSourceTypeId,
    setSelectedSourceDetails,
    setConnectionInputSchema,
    setIsSubmitting,
    setCurrentStep,
    setWizardSelectedSource,
    closeSourceDialog,
    refreshDeviceListing
  });

  const descriptionElementRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (showSourcePopup) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [showSourcePopup]);

  const title = useMemo(
    () =>
      determineDialogTitle(
        mode,
        selectedCloudSource,
        sourceResponse,
        currentStep,
        selectedOrgData?.id || 0
      ),
    [mode, selectedCloudSource, sourceResponse, currentStep, selectedOrgData]
  );

  const buttonLabel = determineButtonLabel(mode, currentStep, WIZARD_STEP);

  // Wizard navigation
  const { handleNext, handleBack, handleSourceSelect } = useWizardNavigation({
    currentStep,
    setCurrentStep,
    wizardSelectedSource,
    setWizardSelectedSource
  });

  // Submission handling
  const { handleCreateSourceSubmit, handleError } = useSourceSubmission({
    mode,
    isSubmitting,
    setIsSubmitting,
    mutation,
    showSourcePopup,
    getValues,
    trigger,
    setError,
    sourceStatus,
    selectedSourceDetails,
    sourceResponse,
    sourceTypes,
    selectedOrgLabel: selectedOrgData.label,
    selectedSubSourceType,
    categories,
    regions,
    cloudProviders,
    authenticationMode,
    newCertificate,
    regenerateCertificate,
    connectionInputSchema,
    setDialogConfig,
    confirmSave,
    getNewlyAddedCategories,
    getNewlyAddedRegions,
    getNewlyAddedCloudProviders,
    getRemovedCategories,
    getRemovedRegions
  });

  // Setup instructions URL computation
  const setupInstructionsUrl = useMemo(
    () =>
      buildSetupInstructionsUrl(
        selectedSourceTypeId,
        originalSourceTypes,
        DOCS_URL,
        CloudAPILinks
      ),
    [selectedSourceTypeId, originalSourceTypes]
  );

  // Wizard data provider pre-population
  useWizardDataProviderPreset({
    mode,
    currentStep,
    wizardSelectedSource,
    connectionInputSchema,
    setValue
  });

  return (
    <>
      <Dialog
        className={classes.dialogContainer}
        open={showSourcePopup}
        onClose={handleClose}
        scroll={scroll}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth
      >
        <DialogTitle id="scroll-dialog-title">
          {(mode === SourceDialogMode.AddThumbPrintId ||
            mode === SourceDialogMode.AddAccessCode) && (
            <div style={{ marginRight: 'auto' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
              <span style={{ marginLeft: '.5rem' }}>{title}</span>
            </div>
          )}
          {mode !== SourceDialogMode.AddThumbPrintId &&
            mode !== SourceDialogMode.AddAccessCode && <span>{title}</span>}
        </DialogTitle>

        <IconButton
          aria-label="Close"
          className={classes.sourceCreateDialogIconButtonCancel}
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            handleClose(e, CLOSE_REASON.BUTTON)
          }
        >
          <FontAwesomeIcon size="xs" icon={CancelIcon} />
        </IconButton>

        {/* Stepper for New mode */}
        {mode === SourceDialogMode.New && selectedOrgData?.id !== 0 && (
          <MuiBox
            className={classes.sourceCreateDialogStepper}
            sx={{ px: 3, pt: 2, pb: 1 }}
          >
            <Stepper activeStep={Math.max(0, currentStep - 1)}>
              <Step>
                <StepLabel>Select Source</StepLabel>
              </Step>
              <Step>
                <StepLabel>Configure Source</StepLabel>
              </Step>
            </Stepper>
          </MuiBox>
        )}

        <DialogContent>
          <FormProvider {...formMethods}>
            <form
              onSubmit={handleSubmit(handleCreateSourceSubmit, handleError)}
            >
              {(mode === SourceDialogMode.New ||
                mode === SourceDialogMode.Delete) &&
                selectedOrgData?.id === 0 && (
                  <DialogContentText component="div" sx={{ pt: 2, pb: 1 }}>
                    <Alert severity="info">
                      <Typography>
                        Please select an organization to add/delete a source.
                      </Typography>
                    </Alert>
                  </DialogContentText>
                )}

              {mode === SourceDialogMode.New &&
                selectedOrgData?.id !== 0 &&
                currentStep === WIZARD_STEP.SELECT_SOURCE && (
                  <SourceSelectionStep
                    selectedSource={wizardSelectedSource}
                    onSourceSelect={handleSourceSelect}
                  />
                )}

              {((mode === SourceDialogMode.New &&
                selectedOrgData?.id !== 0 &&
                currentStep === WIZARD_STEP.CONFIGURE_SOURCE) ||
                mode === SourceDialogMode.Edit) && (
                <SourceCreateForm
                  isCustomerCloudSourcePage={isCustomerCloudSourcePage}
                />
              )}

              {mode === SourceDialogMode.AddThumbPrintId &&
                selectedOrgData?.id !== 0 && <AddThumbprintId />}

              {mode === SourceDialogMode.AddAccessCode &&
                selectedOrgData?.id !== 0 && <AddAccessCode />}

              {selectedOrgData?.id !== 0 &&
                mode === SourceDialogMode.Delete && (
                  <DialogContentText>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Are you sure you want to delete the source{' '}
                      <strong>{selectedSource.source_name}?</strong>
                    </Typography>
                    {selectedCloudSource.source_type_id ===
                      SOURCE_TYPE.MICROSOFT && (
                      <Typography variant="body1">
                        Deleting a Microsoft source will also delete all
                        associated data providers and their certificate details.
                      </Typography>
                    )}
                  </DialogContentText>
                )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              {errors?.data_provider_selection && (
                <Alert sx={{ py: 0 }} severity="error">
                  Please select at least one data provider.
                </Alert>
              )}
            </form>
          </FormProvider>
        </DialogContent>

        <DialogActions>
          <div className={classes.footerMessage}>
            {/* Docs link for step 1 */}
            {currentStep === WIZARD_STEP.SELECT_SOURCE &&
              mode === SourceDialogMode.New &&
              selectedOrgData?.id !== 0 && (
                <Button
                  style={{ marginRight: '1rem' }}
                  variant="outlined"
                  startIcon={
                    <FontAwesomeIcon size="lg" icon={externalLinkIcon} />
                  }
                  href={`${DOCS_URL}/2_main-menu/log/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Log Ingestion
                </Button>
              )}
            {setupInstructionsUrl &&
              currentStep === WIZARD_STEP.CONFIGURE_SOURCE &&
              mode === SourceDialogMode.New && (
                <Button
                  style={{ marginRight: '1rem' }}
                  variant="outlined"
                  startIcon={
                    <FontAwesomeIcon size="lg" icon={externalLinkIcon} />
                  }
                  href={setupInstructionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Setup Instructions
                </Button>
              )}
            {setupInstructionsUrl && mode === SourceDialogMode.Edit && (
              <Button
                style={{ marginRight: '1rem' }}
                variant="outlined"
                startIcon={
                  <FontAwesomeIcon size="lg" icon={externalLinkIcon} />
                }
                href={setupInstructionsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Setup Instructions
              </Button>
            )}
            {selectedOrgData?.id === 0 && mode === SourceDialogMode.Edit && (
              <Typography>
                <FontAwesomeIcon
                  size="lg"
                  style={{ marginRight: '.5rem' }}
                  icon={WarningIcon}
                />
                Changes cannot be made until an organization is selected.
              </Typography>
            )}
            {isAutoSocUnsupportedSourceSelected &&
              mode === SourceDialogMode.New &&
              currentStep === WIZARD_STEP.CONFIGURE_SOURCE && (
                <Typography>
                  <FontAwesomeIcon
                    size="lg"
                    style={{ marginRight: '.5rem' }}
                    icon={WarningIcon}
                  />
                  This source is not supported for Auto-SOC onboarding. You can
                  still proceed with the configuration.
                </Typography>
              )}
          </div>
          {/* Back button for step 2 in New mode */}
          {mode === SourceDialogMode.New &&
            currentStep === WIZARD_STEP.CONFIGURE_SOURCE && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleBack}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  size="lg"
                  style={{ marginRight: '0.5rem' }}
                />
                Back
              </Button>
            )}
          {/* Cancel button for all modes except step 2 in New mode */}
          {!(
            mode === SourceDialogMode.New &&
            currentStep === WIZARD_STEP.CONFIGURE_SOURCE
          ) && (
            <Button
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                handleClose(e, CLOSE_REASON.BUTTON)
              }
            >
              Cancel
            </Button>
          )}
          {/* Next/Submit button for New mode step 1 */}
          {mode === SourceDialogMode.New &&
            currentStep === WIZARD_STEP.SELECT_SOURCE &&
            selectedOrgData?.id !== 0 && (
              <Button
                onClick={handleNext}
                variant="contained"
                color="primary"
                size="small"
                disabled={!wizardSelectedSource}
              >
                Next
                <FontAwesomeIcon
                  icon={faArrowRight}
                  size="lg"
                  style={{ marginLeft: '0.5rem' }}
                />
              </Button>
            )}

          {/* Create/Save/Submit button for other modes and step 2 */}
          {selectedOrgData?.id !== 0 &&
            ((mode === SourceDialogMode.New &&
              currentStep === WIZARD_STEP.CONFIGURE_SOURCE) ||
              mode === SourceDialogMode.Edit ||
              mode === SourceDialogMode.Delete ||
              mode === SourceDialogMode.AddThumbPrintId ||
              mode === SourceDialogMode.AddAccessCode) && (
              <Button
                onClick={formMethods.handleSubmit(
                  handleCreateSourceSubmit,
                  handleError
                )}
                variant="contained"
                color="primary"
                size="small"
                type="submit"
                disabled={mutation.isPending || isSubmitting}
              >
                {mutation.isPending || isSubmitting ? (
                  <CircularProgress color="secondary" size={20} />
                ) : (
                  <span>{buttonLabel}</span>
                )}
              </Button>
            )}
        </DialogActions>
      </Dialog>
      {showSourcePopup && <CreateSourceConfirmationModal />}
    </>
  );
};
export default SourceCreateDialog;
